class CartCrossSaleMoneyFormatter {
  static format(cents) {
    const amount = Number(cents) || 0;
    const moneyFormat = window.spratlyThemeSettings && window.spratlyThemeSettings.money_format;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && moneyFormat) {
      return window.Shopify.formatMoney(amount, moneyFormat);
    }

    return `${new Intl.NumberFormat('ro-RO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount / 100)} lei`;
  }
}

class CartCrossSale {
  constructor(el) {
    this.el = el;
    this.source = el.dataset.source || 'manual';
    this.productId = Number.parseInt(el.dataset.productId, 10) || 0;
    this.limit = Math.min(Number.parseInt(el.dataset.limit, 10) || 5, 5);
    this.recommendationsUrl = el.dataset.recommendationsUrl || '/recommendations/products';
    this.track = el.querySelector('[data-cross-sale-track]');
    this.footer = el.querySelector('[data-cross-sale-footer]');
    this.counter = el.querySelector('[data-cross-sale-counter]');
    this.prevButton = el.querySelector('[data-cross-sale-prev]');
    this.nextButton = el.querySelector('[data-cross-sale-next]');
    this.currentIndex = 0;
    this.itemCount = 0;

    if (!this.track || !this.footer || !this.counter || !this.prevButton || !this.nextButton) return;

    this.handleClick = this.handleClick.bind(this);
    this.init();
  }

  static initAll(root = document) {
    root.querySelectorAll('[data-cart-cross-sale]').forEach((el) => {
      if (!el.__cartCrossSale) {
        el.__cartCrossSale = new CartCrossSale(el);
      }
    });
  }

  async init() {
    if (this.source === 'recommended') {
      await this.loadRecommendedProducts();
    }

    this.refreshSlides();

    if (!this.itemCount) {
      this.el.remove();
      return;
    }

    this.el.addEventListener('click', this.handleClick);
    this.render();
  }

  async loadRecommendedProducts() {
    if (!this.productId) return;

    const url = new URL(`${this.recommendationsUrl}.json`, window.location.origin);
    url.searchParams.set('product_id', String(this.productId));
    url.searchParams.set('limit', String(this.limit));
    url.searchParams.set('intent', 'related');

    try {
      const response = await fetch(url.toString(), {
        headers: {
          Accept: 'application/json',
        },
      });
      const payload = await response.json();

      if (!response.ok) {
        throw payload;
      }

      const products = Array.isArray(payload.products) ? payload.products : [];
      const normalizedProducts = products
        .map((product) => this.normalizeProduct(product))
        .filter(Boolean)
        .slice(0, this.limit);

      if (!normalizedProducts.length) return;

      this.track.innerHTML = normalizedProducts.map((product) => this.buildProductMarkup(product)).join('');
    } catch (error) {
      console.error('[CartCrossSale] recommendations error', error);
    }
  }

  normalizeProduct(product) {
    if (!product || product.available !== true) return null;

    const firstAvailableVariant = Array.isArray(product.variants)
      ? product.variants.find((variant) => variant.available)
      : null;

    if (!firstAvailableVariant) return null;

    const featuredImage = product.featured_image || {};
    const imageSrc = typeof featuredImage === 'string'
      ? featuredImage
      : featuredImage.url || featuredImage.src || '';

    return {
      title: product.title,
      url: product.url,
      image: imageSrc,
      variantId: firstAvailableVariant.id,
      price: firstAvailableVariant.price,
      compareAtPrice: firstAvailableVariant.compare_at_price || 0,
    };
  }

  buildProductMarkup(product) {
    const hasComparePrice = Number(product.compareAtPrice) > Number(product.price);

    return `
      <article class="cart-cross-sale__slide" data-cross-sale-item data-variant-id="${product.variantId}">
        <div class="cart-cross-sale__card">
          <a class="cart-cross-sale__image" href="${product.url}" aria-label="${this.escapeHtml(product.title)}">
            ${product.image ? `<img src="${product.image}" alt="${this.escapeHtml(product.title)}" width="72" height="88" loading="lazy">` : ''}
          </a>
          <div class="cart-cross-sale__content">
            <a class="cart-cross-sale__name" href="${product.url}">${this.escapeHtml(product.title)}</a>
            <div class="cart-cross-sale__prices">
              ${hasComparePrice
                ? `
                  <span class="cart-cross-sale__price cart-cross-sale__price--sale">${CartCrossSaleMoneyFormatter.format(product.price)}</span>
                  <span class="cart-cross-sale__price cart-cross-sale__price--compare">${CartCrossSaleMoneyFormatter.format(product.compareAtPrice)}</span>
                `
                : `<span class="cart-cross-sale__price">${CartCrossSaleMoneyFormatter.format(product.price)}</span>`}
            </div>
          </div>
          <button class="cart-cross-sale__add" type="button" data-cross-sale-add data-variant-id="${product.variantId}">
            <span class="cart-cross-sale__add-label">${this.getAddButtonText()}</span>
          </button>
        </div>
      </article>
    `;
  }

  getAddButtonText() {
    const themeStrings = window.spratlyThemeStrings || {};
    return themeStrings.addToCart || 'Add to cart';
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  refreshSlides() {
    this.items = Array.from(this.track.querySelectorAll('[data-cross-sale-item]'));
    this.itemCount = this.items.length;
    this.currentIndex = Math.min(this.currentIndex, Math.max(this.itemCount - 1, 0));
  }

  handleClick(event) {
    const prevButton = event.target.closest('[data-cross-sale-prev]');
    const nextButton = event.target.closest('[data-cross-sale-next]');
    const addButton = event.target.closest('[data-cross-sale-add]');

    if (prevButton) {
      event.preventDefault();
      this.goTo(this.currentIndex - 1);
      return;
    }

    if (nextButton) {
      event.preventDefault();
      this.goTo(this.currentIndex + 1);
      return;
    }

    if (addButton) {
      event.preventDefault();
      this.addVariant(addButton);
    }
  }

  goTo(index) {
    if (!this.itemCount) return;
    this.currentIndex = Math.max(0, Math.min(index, this.itemCount - 1));
    this.render();
  }

  render() {
    this.track.style.transform = `translate3d(-${this.currentIndex * 100}%, 0, 0)`;
    this.counter.textContent = `${this.currentIndex + 1}/${this.itemCount}`;
    this.prevButton.disabled = this.currentIndex === 0;
    this.nextButton.disabled = this.currentIndex >= this.itemCount - 1;
    this.footer.hidden = this.itemCount <= 1;
  }

  async addVariant(button) {
    const variantId = Number.parseInt(button.dataset.variantId, 10);

    if (!variantId) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
        }),
      });

      const payload = await response.json();

      if (!response.ok) {
        throw payload;
      }

      const addedItem = Array.isArray(payload.items) ? payload.items[0] : payload;

      if (addedItem && window.Shopify && typeof window.Shopify.onItemAdded === 'function') {
        window.Shopify.onItemAdded(addedItem);
      }
    } catch (error) {
      console.error('[CartCrossSale] addVariant error', error);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

class CartCrossSaleBootstrap {
  constructor() {
    this.init();
  }

  init() {
    const run = (root = document) => {
      CartCrossSale.initAll(root);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => run(), { once: true });
    } else {
      run();
    }

    if (!document.body) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          if (node.matches('[data-cart-cross-sale]')) {
            run(node.parentElement || document);
            return;
          }

          if (node.querySelector('[data-cart-cross-sale]')) {
            run(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}

if (!window.__cartCrossSaleBootstrap) {
  window.__cartCrossSaleBootstrap = new CartCrossSaleBootstrap();
}
